import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  LinearProgress,
  Alert,
  Badge,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Business,
  Store,
  Person,
  Email,
  Phone,
  LocationOn,
  AccountBalance,
  CalendarToday,
  Edit,
  Key,
  Block,
  CheckCircle,
  AttachMoney,
  History,
  Receipt,
  AccountBalanceWallet,
  Settings,
  Warning,
  Refresh,
  Download,
  Timeline as TimelineIcon,
  TrendingUp,
  Delete,
  Update,
  ConfirmationNumber,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { partnerService, transactionService, settlementService } from '../services/api';
import { parseDate } from '../utils/dateUtils';
import { getErrorMessage } from '../utils/errorUtils';
import StatusHistory from '../components/StatusHistory';
import PartnerTransactions from '../components/PartnerTransactions';
import PartnerAccount from '../components/PartnerAccount';
import PartnerSettlements from '../components/PartnerSettlements';
import PartnerAccounts from '../components/PartnerAccounts';
import PartnerVouchers from '../components/PartnerVouchers';
import ApiKeyManagement from '../components/ApiKeyManagement';
import ConfirmDialog from '../components/ConfirmDialog';
import NotificationSnackbar from '../components/NotificationSnackbar';

interface Partner {
  id: string;
  partnerCode: string;
  partnerName: string;
  partnerType: 'MERCHANT' | 'RETAILER' | 'AGENT';
  partnerStatus: 'DRAFT' | 'PENDING_APPROVAL' | 'ACTIVE' | 'SUSPENDED' | 'TERMINATED' | 'PENDING_ONBOARDING';

  // Contact information
  email: string;
  phoneNumber: string;
  alternativePhone?: string;

  // Business address
  businessAddress: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;

  // Primary contact
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;

  // Business details
  businessRegistrationNumber?: string;
  taxIdentificationNumber?: string;

  // Financial
  defaultCommissionRate: number;
  specialCommissionRate?: number;
  commissionCap?: number;
  minimumSettlementAmount?: number;
  balance?: number;
  floatBalance?: number;
  settlementFrequency: string;
  settlementDelayDays?: number;
  transactionLimitDaily?: number;
  transactionLimitMonthly?: number;
  riskLevel?: string;

  // API & Security
  apiKey?: string;
  apiKeyGenerated?: boolean;
  callbackUrl?: string;

  // Status & timestamps
  status?: string;
  version?: string;
  onboardedAt?: string;
  verifiedAt?: string;
  suspendedAt?: string;
  suspensionReason?: string;
  terminatedAt?: string;
  totalTransactions?: number;
  totalVolume?: number;
  createdAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  taxId?: string;
  registrationNumber?: string;
  dateCreated?: string;
  lastUpdated?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const PartnerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState<any>({
    totalTransactions: 0,
    totalVolume: 0,
    currentBalance: 0,
    commissionRate: 0,
  });

  // Dialog and notification states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'confirm' as any,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    inputLabel: '',
    inputPlaceholder: '',
    inputRequired: false,
    onConfirm: (inputValue?: string) => {},
  });

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success' as any,
  });

  useEffect(() => {
    if (id) {
      fetchPartnerDetails();
      fetchPartnerSummary();
    }
  }, [id]);

  const fetchPartnerDetails = async () => {
    setLoading(true);
    try {
      const response = await partnerService.getPartner(id!);
      setPartner(response);
    } catch (error) {
      console.error('Failed to fetch partner details:', error);
      const errorMessage = getErrorMessage(error, 'Failed to load partner details');
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerSummary = async () => {
    try {
      const response = await partnerService.getPartnerSummary(id!);
      setSummary(response);
    } catch (error) {
      console.error('Failed to fetch partner summary:', error);
      const errorMessage = getErrorMessage(error, 'Failed to load partner summary');
      showNotification(errorMessage, 'error');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPartnerDetails();
    await fetchPartnerSummary();
    setRefreshing(false);
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, type });
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: (inputValue?: string) => void,
    options: Partial<typeof confirmDialog> = {}
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      type: 'confirm',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      inputLabel: '',
      inputPlaceholder: '',
      inputRequired: false,
      onConfirm,
      ...options,
    });
  };

  const getPartnerIcon = (type: Partner['partnerType']) => {
    switch (type) {
      case 'MERCHANT': return <Business />;
      case 'RETAILER': return <Store />;
      case 'AGENT': return <Person />;
    }
  };

  const getStatusColor = (status: Partner['partnerStatus']) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'SUSPENDED': return 'warning';
      case 'TERMINATED': return 'error';
      case 'PENDING_ONBOARDING': return 'info';
      case 'PENDING_APPROVAL': return 'info';
      case 'DRAFT': return 'default';
      default: return 'default';
    }
  };

  const handleStatusChange = async (action: string) => {
    // Implementation for status changes
    switch (action) {
      case 'approve':
        // Handle approval
        break;
      case 'suspend':
        // Handle suspension
        break;
      case 'activate':
        // Handle activation
        break;
      case 'terminate':
        // Handle termination
        break;
    }
  };

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>Loading partner details...</Typography>
        </Box>
      </Box>
    );
  }

  if (!partner) {
    return (
      <Box>
        <Alert severity="error">Partner not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/partners')}>
          Back to Partners
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Dashboard
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/partners')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Partners
        </Link>
        <Typography color="text.primary">{partner.partnerName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/partners')}>
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            {getPartnerIcon(partner.partnerType)}
          </Avatar>
          <Box>
            <Typography variant="h4">{partner.partnerName}</Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={partner.partnerStatus}
                color={getStatusColor(partner.partnerStatus) as any}
                size="small"
              />
              <Chip label={partner.partnerType} variant="outlined" size="small" />
              <Typography variant="caption" color="text.secondary">
                Code: {partner.partnerCode}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => {/* Export partner data */}}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => navigate(`/partners/${id}/delivery-preferences`)}
          >
            Delivery
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/partners/${id}/edit`)}
          >
            Edit Partner
          </Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Transactions
                  </Typography>
                  <Typography variant="h5">
                    {summary.totalTransactions?.toLocaleString() || '0'}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light' }}>
                  <Receipt />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Volume
                  </Typography>
                  <Typography variant="h5">
                    ${summary.totalVolume?.toLocaleString() || '0'}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light' }}>
                  <AttachMoney />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Current Balance
                  </Typography>
                  {/* BUG006: show per-currency balances (matching the Accounts view) rather than a
                      cross-currency sum, which is meaningless and never matched the Accounts tab. */}
                  {Array.isArray(summary.accountBalances) && summary.accountBalances.length > 0 ? (
                    summary.accountBalances.map((ab: any) => (
                      <Typography key={ab.currency} variant="h6">
                        {ab.currency}{' '}
                        {Number(ab.currentBalance ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="h5">
                      {Number(summary.currentBalance ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  )}
                </Box>
                <Avatar sx={{ bgcolor: 'info.light' }}>
                  <AccountBalanceWallet />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Commission Rate
                  </Typography>
                  <Typography variant="h5">
                    {summary.commissionRate || partner?.specialCommissionRate || partner?.defaultCommissionRate || 0}%
                  </Typography>
                  {partner?.specialCommissionRate && (
                    <Chip label="Custom" size="small" color="secondary" />
                  )}
                </Box>
                <Avatar sx={{ bgcolor: 'warning.light' }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" icon={<Business />} iconPosition="start" />
          <Tab
            label="Status History"
            icon={
              <Badge badgeContent="!" color="info">
                <TimelineIcon />
              </Badge>
            }
            iconPosition="start"
          />
          <Tab label="Transactions" icon={<Receipt />} iconPosition="start" />
          <Tab label="Vouchers" icon={<ConfirmationNumber />} iconPosition="start" />
          <Tab label="Accounts" icon={<AccountBalance />} iconPosition="start" />
          <Tab label="Account Summary" icon={<AccountBalanceWallet />} iconPosition="start" />
          <Tab label="Settlements" icon={<AccountBalance />} iconPosition="start" />
          <Tab label="API & Security" icon={<Key />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><Business /></ListItemIcon>
                      <ListItemText
                        primary="Business Name"
                        secondary={partner.partnerName}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Email /></ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={partner.email}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Phone /></ListItemIcon>
                      <ListItemText
                        primary="Phone"
                        secondary={partner.phoneNumber}
                      />
                    </ListItem>
                    {partner.alternativePhone && (
                      <ListItem>
                        <ListItemIcon><Phone /></ListItemIcon>
                        <ListItemText
                          primary="Alternative Phone"
                          secondary={partner.alternativePhone}
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon><LocationOn /></ListItemIcon>
                      <ListItemText
                        primary="Address"
                        secondary={`${partner.businessAddress}${partner.city ? `, ${partner.city}` : ''}${partner.country ? `, ${partner.country}` : ''}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CalendarToday /></ListItemIcon>
                      <ListItemText
                        primary="Date Created"
                        secondary={partner.dateCreated ? format(parseDate(partner.dateCreated), 'PPP p') : 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Update /></ListItemIcon>
                      <ListItemText
                        primary="Last Updated"
                        secondary={partner.lastUpdated ? format(parseDate(partner.lastUpdated), 'PPP p') : 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Person /></ListItemIcon>
                      <ListItemText
                        primary="Primary Contact"
                        secondary={partner.primaryContactName || 'Not specified'}
                      />
                    </ListItem>
                    {partner.primaryContactEmail && (
                      <ListItem>
                        <ListItemIcon><Email /></ListItemIcon>
                        <ListItemText
                          primary="Primary Contact Email"
                          secondary={partner.primaryContactEmail}
                        />
                      </ListItem>
                    )}
                    {partner.primaryContactPhone && (
                      <ListItem>
                        <ListItemIcon><Phone /></ListItemIcon>
                        <ListItemText
                          primary="Primary Contact Phone"
                          secondary={partner.primaryContactPhone}
                        />
                      </ListItem>
                    )}
                    {partner.stateProvince && (
                      <ListItem>
                        <ListItemIcon><LocationOn /></ListItemIcon>
                        <ListItemText
                          primary="State/Province"
                          secondary={partner.stateProvince}
                        />
                      </ListItem>
                    )}
                    {partner.postalCode && (
                      <ListItem>
                        <ListItemIcon><LocationOn /></ListItemIcon>
                        <ListItemText
                          primary="Postal Code"
                          secondary={partner.postalCode}
                        />
                      </ListItem>
                    )}
                    {partner.businessAddress && (
                      <ListItem>
                        <ListItemIcon><LocationOn /></ListItemIcon>
                        <ListItemText
                          primary="Business Address"
                          secondary={partner.businessAddress}
                        />
                      </ListItem>
                    )}
                    {partner.city && (
                      <ListItem>
                        <ListItemIcon><LocationOn /></ListItemIcon>
                        <ListItemText
                          primary="City"
                          secondary={partner.city}
                        />
                      </ListItem>
                    )}
                    {partner.country && (
                      <ListItem>
                        <ListItemIcon><LocationOn /></ListItemIcon>
                        <ListItemText
                          primary="Country"
                          secondary={partner.country}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Business Details
                  </Typography>
                  <List>
                    {partner.businessRegistrationNumber && (
                      <ListItem>
                        <ListItemIcon><Business /></ListItemIcon>
                        <ListItemText
                          primary="Business Registration Number"
                          secondary={partner.businessRegistrationNumber}
                        />
                      </ListItem>
                    )}
                    {partner.taxIdentificationNumber && (
                      <ListItem>
                        <ListItemIcon><Receipt /></ListItemIcon>
                        <ListItemText
                          primary="Tax Identification Number"
                          secondary={partner.taxIdentificationNumber}
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon><Settings /></ListItemIcon>
                      <ListItemText
                        primary="Settlement Frequency"
                        secondary={partner.settlementFrequency}
                      />
                    </ListItem>
                    {partner.settlementDelayDays !== undefined && (
                      <ListItem>
                        <ListItemIcon><CalendarToday /></ListItemIcon>
                        <ListItemText
                          primary="Settlement Delay Days"
                          secondary={`${partner.settlementDelayDays} days`}
                        />
                      </ListItem>
                    )}
                    {partner.minimumSettlementAmount !== undefined && partner.minimumSettlementAmount !== null && (
                      <ListItem>
                        <ListItemIcon><AttachMoney /></ListItemIcon>
                        <ListItemText
                          primary="Minimum Settlement Amount"
                          secondary={`$${partner.minimumSettlementAmount.toLocaleString()}`}
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon><AttachMoney /></ListItemIcon>
                      <ListItemText
                        primary="Commission Rate"
                        secondary={`${partner.defaultCommissionRate}%${partner.specialCommissionRate ? ` (Special: ${partner.specialCommissionRate}%)` : ''}`}
                      />
                    </ListItem>
                    {partner.commissionCap !== undefined && partner.commissionCap !== null && (
                      <ListItem>
                        <ListItemIcon><AttachMoney /></ListItemIcon>
                        <ListItemText
                          primary="Commission Cap"
                          secondary={`$${partner.commissionCap.toLocaleString()}`}
                        />
                      </ListItem>
                    )}
                    {partner.callbackUrl && (
                      <ListItem>
                        <ListItemIcon><Settings /></ListItemIcon>
                        <ListItemText
                          primary="Callback URL"
                          secondary={partner.callbackUrl}
                        />
                      </ListItem>
                    )}
                    {partner.verifiedAt && (
                      <ListItem>
                        <ListItemIcon><CheckCircle /></ListItemIcon>
                        <ListItemText
                          primary="Verified Date"
                          secondary={format(parseDate(partner.verifiedAt), 'PPP p')}
                        />
                      </ListItem>
                    )}
                    {partner.approvedAt && (
                      <ListItem>
                        <ListItemIcon><CheckCircle /></ListItemIcon>
                        <ListItemText
                          primary="Approved Date"
                          secondary={format(parseDate(partner.approvedAt), 'PPP p')}
                        />
                      </ListItem>
                    )}
                    {partner.approvedBy && (
                      <ListItem>
                        <ListItemIcon><Person /></ListItemIcon>
                        <ListItemText
                          primary="Approved By"
                          secondary={partner.approvedBy}
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon><CheckCircle /></ListItemIcon>
                      <ListItemText
                        primary="Onboarded Date"
                        secondary={partner.onboardedAt ? format(parseDate(partner.onboardedAt), 'PPP p') : 'Not yet onboarded'}
                      />
                    </ListItem>
                    {partner.suspendedAt && (
                      <ListItem>
                        <ListItemIcon><Block /></ListItemIcon>
                        <ListItemText
                          primary="Suspended Date"
                          secondary={format(parseDate(partner.suspendedAt), 'PPP p')}
                        />
                      </ListItem>
                    )}
                    {partner.suspensionReason && (
                      <ListItem>
                        <ListItemIcon><Warning /></ListItemIcon>
                        <ListItemText
                          primary="Suspension Reason"
                          secondary={partner.suspensionReason}
                        />
                      </ListItem>
                    )}
                    {partner.terminatedAt && (
                      <ListItem>
                        <ListItemIcon><Delete /></ListItemIcon>
                        <ListItemText
                          primary="Terminated Date"
                          secondary={format(parseDate(partner.terminatedAt), 'PPP p')}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Grid>

              {/* System Information */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    System Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Partner ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {partner.id}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Partner Code
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {partner.partnerCode}
                        </Typography>
                      </Box>
                    </Grid>
                    {partner.status && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Status
                          </Typography>
                          <Typography variant="body2">
                            {partner.status}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {partner.version && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Version
                          </Typography>
                          <Typography variant="body2">
                            {partner.version}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* Risk and Limits */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Risk & Limits
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Risk Level
                        </Typography>
                        <Typography variant="body1">
                          <Chip
                            label={partner.riskLevel || 'Standard'}
                            color={partner.riskLevel === 'HIGH' ? 'error' : partner.riskLevel === 'MEDIUM' ? 'warning' : 'success'}
                            size="small"
                          />
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Daily Transaction Limit
                        </Typography>
                        <Typography variant="body1">
                          ${partner.transactionLimitDaily?.toLocaleString() || 'Unlimited'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Monthly Transaction Limit
                        </Typography>
                        <Typography variant="body1">
                          ${partner.transactionLimitMonthly?.toLocaleString() || 'Unlimited'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Settlement Delay
                        </Typography>
                        <Typography variant="body1">
                          {partner.settlementDelayDays ? `${partner.settlementDelayDays} days` : 'Standard'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Status History Tab */}
          <TabPanel value={tabValue} index={1}>
            <StatusHistory partnerId={partner.id} />
          </TabPanel>

          {/* Transactions Tab */}
          <TabPanel value={tabValue} index={2}>
            <PartnerTransactions partnerId={partner.id} />
          </TabPanel>

          {/* Vouchers Tab */}
          <TabPanel value={tabValue} index={3}>
            <PartnerVouchers partnerId={partner.id} partnerType={partner.partnerType} />
          </TabPanel>

          {/* Accounts Tab */}
          <TabPanel value={tabValue} index={4}>
            <PartnerAccounts partnerId={partner.id} partnerType={partner.partnerType} />
          </TabPanel>

          {/* Account Summary Tab */}
          <TabPanel value={tabValue} index={5}>
            <PartnerAccount partner={partner} />
          </TabPanel>

          {/* Settlements Tab */}
          <TabPanel value={tabValue} index={6}>
            <PartnerSettlements partnerId={partner.id} />
          </TabPanel>

          {/* API & Security Tab */}
          <TabPanel value={tabValue} index={7}>
            <ApiKeyManagement
              partnerId={partner.id}
              callbackUrl={partner.callbackUrl}
              partnerType={partner.partnerType}
            />
          </TabPanel>
        </Box>
      </Paper>

      {/* Dialogs */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        inputLabel={confirmDialog.inputLabel}
        inputPlaceholder={confirmDialog.inputPlaceholder}
        inputRequired={confirmDialog.inputRequired}
        onConfirm={(inputValue) => {
          confirmDialog.onConfirm(inputValue);
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Box>
  );
};

export default PartnerDetail;